import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, Collapse, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Google from "@mui/icons-material/Google";
import {
  AlertTriangle,
  Award,
  CalendarDays,
  ChevronDown,
  CircleArrowRight,
  ClipboardCheck,
  ExternalLink,
  Info,
  Link2,
  Plus,
  XCircle,
} from "lucide-react";

import { Button } from "../../../components/Button";
import { Card, CardContent } from "../../../components/Card";
import { CustomTextField } from "../../../components/CustomTextField";
import { CustomTextArea } from "../../../components/CustomTextArea";
import { CustomSelector } from "../../../components/CustomSelector";
import { ToggleSwitch } from "../../../components/ToggleSwitch";
import { Label } from "../../../components/Label";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import { Dialog, DialogActions, DialogContent, DialogHeader, DialogTitle } from "../../../components/Dialog";
import SuccessPopup from "../../../components/SuccessPopup";
import { ScoreGauge } from "./ScoreGauge";
import {
  getSiteAuditIssuePages,
  getSiteAuditPages,
  getSiteAuditReport,
  getSiteAudits,
  launchSiteAudit,
} from "../../../services/metricsService";

// Catálogo de SE Ranking: el índice es el valor que espera la API.
const USER_AGENTS = [
  "SEBot-WA",
  "Googlebot",
  "Googlebot Image",
  "Bingbot",
  "Yahoo! Slurp",
  "YandexBot",
  "BaiduSpider",
  "Chrome (Windows)",
  "Chrome (macOS)",
  "Chrome (iOS)",
  "Firefox (Windows)",
  "Firefox (Linux)",
  "Safari (macOS)",
  "Opera (Windows)",
].map((label, index) => ({ value: index, label }));

const URL_RULES = [
  { key: "allow", label: "Permitir únicamente la comprobación de páginas cuyas URL contengan:", placeholder: "Por ejemplo, /blog/" },
  { key: "disallow", label: "No permitir la comprobación de páginas cuyas URL contengan:", placeholder: "Por ejemplo, /blog/otro/" },
  { key: "hide", label: "Ocultar URL y recursos que contengan:", placeholder: "Por ejemplo, /contactos/" },
];

const SEVERITIES = [
  { key: "error", total: "errors", label: "Errores", color: "error.main", Icon: XCircle },
  { key: "warning", total: "warnings", label: "Advertencias", color: "warning.dark", Icon: AlertTriangle },
  { key: "notice", total: "notices", label: "Avisos", color: "#94A3B8", Icon: Info },
];
const SEVERITY_BY_KEY = Object.fromEntries(SEVERITIES.map((severity) => [severity.key, severity]));
// Las props de color fuera de `sx` (iconos) no entienden rutas del tema como "error.main".
const resolveColor = (palette, color) => color.split(".").reduce((node, key) => node?.[key], palette) ?? color;

const RECOMMENDED_SCORE = 90;
// Rangos de puntuación de SE Ranking, distintos de los de Lighthouse.
const SCORE_BANDS = [
  { min: 90, label: "Excelente", color: (palette) => palette.success.main },
  { min: 80, label: "Bueno", color: (palette) => palette.success.light },
  { min: 70, label: "Decente", color: (palette) => palette.warning.light },
  { min: 40, label: "Mediocre", color: (palette) => palette.warning.dark },
  { min: 0, label: "Malo", color: (palette) => palette.error.main },
];
const scoreBand = (score) => SCORE_BANDS.find(({ min }) => (score ?? 0) >= min);
const TOP_ISSUES = 5;
const REPORT_POLL_MS = 10000;
const FINAL_STATUSES = ["finished", "cancelled", "expired"];

const STATUS_LABELS = {
  queued: "En cola",
  processing: "Analizando",
  cancelled: "Cancelada",
  expired: "Caducada",
};

const BLOCK_HELP = {
  score:
    "Puntuación de la salud técnica del sitio, de 0 a 100, calculada según el número de problemas y su gravedad. Rangos: 0-39 malo, 40-69 mediocre, 70-79 decente, 80-89 bueno y 90-100 excelente. Lo recomendable es mantenerla por encima de 90.",
  types:
    "Problemas encontrados en el sitio clasificados por tipo: Errores (gravedad alta), Advertencias (gravedad media) y Avisos (gravedad baja). Cada página afectada cuenta como un problema. Céntrate primero en los Errores y luego en las Advertencias; los Avisos son mejoras opcionales.",
  crawl:
    "Páginas que ha rastreado el robot y cuántas comprobaciones técnicas se han superado o tienen al menos una página con problemas.",
  issues:
    "Comprobaciones con problemas, ordenadas por gravedad y por número de páginas afectadas. Pulsa una fila para ver en qué páginas ocurre.",
  categories: "Problemas por área del sitio. Cada página afectada cuenta como un problema.",
  domain:
    "Autoridad y enlaces del dominio según la base de datos de SE Ranking. Domain Trust va de 0 a 100: cuanto más alto, más confianza transmite el dominio.",
};

const CARD_SX = { padding: { xs: 2, sm: 2.5 }, borderRadius: "12px", minWidth: 0 };

const hostOf = (website) => {
  try {
    return new URL(website.includes("://") ? website : `https://${website}`).hostname;
  } catch {
    return website || "";
  }
};

const emptyForm = (website) => ({
  domain: hostOf(website || ""),
  title: "",
  user_agent: 0,
  js_rendering: false,
  allow: "",
  disallow: "",
  hide: "",
});

const launchError = (err) => {
  const data = err?.response?.data;
  if (data?.code === "audit_limit") return data.detail;
  if (data?.domain) return [].concat(data.domain)[0];
  if (err?.response?.status === 502) {
    return "El servicio de auditoría no está disponible ahora mismo. Vuelve a intentarlo en unos minutos.";
  }
  return "No se ha podido lanzar la auditoría. Vuelve a intentarlo.";
};

function BlockTitle({ children, help, action }) {
  return (
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
          {children}
        </Typography>
        {help && (
          <Tooltip title={help} arrow placement="top">
            <Box component="span" sx={{ display: "inline-flex", color: "text.secondary", cursor: "help" }}>
              <Info size={13} />
            </Box>
          </Tooltip>
        )}
      </Stack>
      {action}
    </Stack>
  );
}

BlockTitle.propTypes = { children: PropTypes.node.isRequired, help: PropTypes.string, action: PropTypes.node };

function Block({ title, help, action, children, sx }) {
  const theme = useTheme();
  return (
    <Box sx={{ ...CARD_SX, border: `1px solid ${theme.palette.divider}`, backgroundColor: "background.paper", ...sx }}>
      <BlockTitle help={help} action={action}>
        {title}
      </BlockTitle>
      {children}
    </Box>
  );
}

Block.propTypes = {
  title: PropTypes.string.isRequired,
  help: PropTypes.string,
  action: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
};

function LegendRow({ color, label, value }) {
  return (
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color }} />
        <Typography variant="body2">{label}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value ?? "—"}
      </Typography>
    </Stack>
  );
}

LegendRow.propTypes = { color: PropTypes.string.isRequired, label: PropTypes.string.isRequired, value: PropTypes.node };

/** Barra apilada errores / advertencias / avisos. */
function SeverityBar({ counts, height = 10 }) {
  const theme = useTheme();
  const total = SEVERITIES.reduce((sum, { key }) => sum + (counts[key] || 0), 0);
  return (
    <Box sx={{ display: "flex", gap: "3px", height, borderRadius: 1, overflow: "hidden", backgroundColor: theme.palette.action.hover }}>
      {total > 0 &&
        SEVERITIES.map(({ key, label, color }) =>
          counts[key] ? (
            <Tooltip
              key={key}
              arrow
              placement="top"
              title={
                <>
                  {label}: <b>{counts[key]}</b>
                </>
              }
            >
              <Box sx={{ flex: counts[key], backgroundColor: color, cursor: "default" }} />
            </Tooltip>
          ) : null
        )}
    </Box>
  );
}

SeverityBar.propTypes = { counts: PropTypes.object.isRequired, height: PropTypes.number };

const pathOf = (url) => url.replace(/^https?:\/\/[^/]+/, "") || "/";

function IssuePages({ clientId, auditId, code }) {
  const [urls, setUrls] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (offset) => {
      setLoading(true);
      setError("");
      try {
        const data = await getSiteAuditIssuePages(clientId, auditId, code, offset);
        setUrls((previous) => (offset ? [...previous, ...data.urls] : data.urls));
        setTotal(data.total);
      } catch (err) {
        console.error("Site audit issue pages could not be read", err?.response?.data || err);
        setError("No se han podido cargar las páginas.");
      } finally {
        setLoading(false);
      }
    },
    [clientId, auditId, code]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  if (error) {
    return (
      <Typography variant="caption" sx={{ color: "error.main" }}>
        {error}
      </Typography>
    );
  }
  if (total === null) return <Skeleton variant="rounded" height={48} />;

  return (
    <Box>
      <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
        {urls.map((url) => (
          <Stack
            key={url}
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={url}
            direction="row"
            spacing={0.75}
            sx={{
              alignItems: "center",
              py: 0.5,
              color: "text.secondary",
              textDecoration: "none",
              "&:hover": { color: "primary.main" },
            }}
          >
            <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
              {pathOf(url)}
            </Typography>
            <ExternalLink size={12} style={{ flexShrink: 0 }} />
          </Stack>
        ))}
      </Box>
      {urls.length < total && (
        <Button variant="ghost" size="sm" onClick={() => load(urls.length)} disabled={loading} sx={{ mt: 0.5 }}>
          {loading ? <CircularProgress size={14} color="inherit" /> : `Ver más (${total - urls.length})`}
        </Button>
      )}
    </Box>
  );
}

IssuePages.propTypes = {
  clientId: PropTypes.number.isRequired,
  auditId: PropTypes.number.isRequired,
  code: PropTypes.string.isRequired,
};

function IssueRow({ issue, clientId, auditId }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { Icon, color } = SEVERITY_BY_KEY[issue.status];
  return (
    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, "&:last-child": { borderBottom: 0 } }}>
      <Stack
        direction="row"
        spacing={1.5}
        onClick={() => setOpen((current) => !current)}
        sx={{ alignItems: "center", py: 1, cursor: "pointer", "&:hover": { backgroundColor: theme.palette.action.hover } }}
      >
        <Icon size={16} color={resolveColor(theme.palette, color)} style={{ flexShrink: 0 }} />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
          {issue.name}
        </Typography>
        <Tooltip title="Páginas afectadas" arrow placement="top">
          <Typography variant="body2" sx={{ fontWeight: 600, color }}>
            {issue.pages}
          </Typography>
        </Tooltip>
        <ChevronDown
          size={16}
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
        />
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 3.5, pb: 1 }}>
          <IssuePages clientId={clientId} auditId={auditId} code={issue.code} />
        </Box>
      </Collapse>
    </Box>
  );
}

IssueRow.propTypes = {
  issue: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  auditId: PropTypes.number.isRequired,
};

function IssuesBlock({ issues, clientId, auditId }) {
  const [showAll, setShowAll] = useState(false);
  const top = issues.filter((issue) => issue.status !== "notice").slice(0, TOP_ISSUES);
  const byCategory = issues.reduce((groups, issue) => {
    (groups[issue.category] ||= []).push(issue);
    return groups;
  }, {});

  return (
    <Block
      title="Problemas principales"
      help={BLOCK_HELP.issues}
      action={
        issues.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setShowAll((open) => !open)}>
            {showAll ? "Ver menos" : `Ver todo (${issues.length})`}
          </Button>
        )
      }
    >
      {issues.length === 0 && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No se han encontrado problemas.
        </Typography>
      )}
      {!showAll &&
        top.map((issue) => <IssueRow key={issue.code} issue={issue} clientId={clientId} auditId={auditId} />)}
      {showAll &&
        Object.entries(byCategory).map(([category, rows]) => (
          <Box key={category} sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              {category}
            </Typography>
            {rows.map((issue) => (
              <IssueRow key={issue.code} issue={issue} clientId={clientId} auditId={auditId} />
            ))}
          </Box>
        ))}
    </Block>
  );
}

IssuesBlock.propTypes = {
  issues: PropTypes.array.isRequired,
  clientId: PropTypes.number.isRequired,
  auditId: PropTypes.number.isRequired,
};

function CategoriesBlock({ categories }) {
  return (
    <Block title="Distribución por categoría" help={BLOCK_HELP.categories}>
      <Stack spacing={1.25}>
        {categories.map((category) => {
          const total = category.error + category.warning + category.notice;
          return (
            <Box key={category.key}>
              <Stack direction="row" sx={{ justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2">{category.name}</Typography>
                <Typography variant="body2" sx={{ color: total ? "text.primary" : "success.main", fontWeight: 600 }}>
                  {total || "✓"}
                </Typography>
              </Stack>
              <SeverityBar counts={category} height={6} />
            </Box>
          );
        })}
      </Stack>
    </Block>
  );
}

CategoriesBlock.propTypes = { categories: PropTypes.array.isRequired };

function DomainRow({ Icon, label, value }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", py: 1 }}>
      <Box sx={{ display: "inline-flex", color: "text.secondary", flexShrink: 0 }}>
        <Icon size={18} style={{ fontSize: 18 }} />
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 16, borderBottom: `1px solid ${theme.palette.divider}` }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value ?? "—"}
      </Typography>
    </Stack>
  );
}

DomainRow.propTypes = { Icon: PropTypes.elementType.isRequired, label: PropTypes.string.isRequired, value: PropTypes.node };

function DomainBlock({ domain }) {
  const columns = [
    [
      { Icon: CalendarDays, label: "Caducidad del dominio", value: domain.expires },
      { Icon: Link2, label: "Backlinks", value: domain.backlinks },
      { Icon: CircleArrowRight, label: "Dominios de referencia", value: domain.referring_domains },
    ],
    [
      { Icon: Award, label: "Domain Trust", value: domain.trust },
      { Icon: Google, label: "Páginas en Google", value: domain.google_pages },
    ],
  ];
  return (
    <Block title="Métricas del dominio" help={BLOCK_HELP.domain}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, columnGap: 4 }}>
        {columns.map((rows) => (
          <Box key={rows[0].label}>
            {rows.map((row) => (
              <DomainRow key={row.label} {...row} />
            ))}
          </Box>
        ))}
      </Box>
    </Block>
  );
}

DomainBlock.propTypes = { domain: PropTypes.object.isRequired };

function CrawledPagesModal({ open, onOpenChange, clientId, auditId }) {
  const theme = useTheme();
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (offset) => {
      setLoading(true);
      setError("");
      try {
        const data = await getSiteAuditPages(clientId, auditId, offset);
        setPages((previous) => (offset ? [...previous, ...data.pages] : data.pages));
        setTotal(data.total);
      } catch (err) {
        console.error("Site audit pages could not be read", err?.response?.data || err);
        setError("No se han podido cargar las páginas rastreadas.");
      } finally {
        setLoading(false);
      }
    },
    [clientId, auditId]
  );

  useEffect(() => {
    if (open && total === null) load(0);
  }, [open, total, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} fullWidth>
      <DialogHeader>
        <DialogTitle>Páginas rastreadas{total !== null ? ` (${total})` : ""}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && total === null && <Skeleton variant="rounded" height={160} />}
        {pages.map((page) => (
          <Stack
            key={page.url}
            component="a"
            href={page.url}
            target="_blank"
            rel="noopener noreferrer"
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: "center",
              py: 1,
              color: "text.primary",
              textDecoration: "none",
              borderBottom: `1px solid ${theme.palette.divider}`,
              "&:last-of-type": { borderBottom: 0 },
              "&:hover .page-path": { color: "primary.main" },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                <Typography className="page-path" variant="body2" noWrap sx={{ minWidth: 0, fontWeight: 500 }}>
                  {pathOf(page.url)}
                </Typography>
                <ExternalLink size={12} style={{ flexShrink: 0 }} />
              </Stack>
              {page.title && (
                <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
                  {page.title}
                </Typography>
              )}
            </Box>
            <Tooltip title="Código de respuesta HTTP" arrow placement="top">
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: String(page.status).startsWith("2") ? "text.secondary" : "error.main" }}
              >
                {page.status}
              </Typography>
            </Tooltip>
          </Stack>
        ))}
        {total !== null && pages.length < total && (
          <Button variant="ghost" size="sm" onClick={() => load(pages.length)} disabled={loading} sx={{ mt: 1 }}>
            {loading ? <CircularProgress size={14} color="inherit" /> : `Ver más (${total - pages.length})`}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

CrawledPagesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired,
  auditId: PropTypes.number.isRequired,
};

function AuditReport({ clientId, auditId }) {
  const { palette } = useTheme();
  const [pagesOpen, setPagesOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    let timer = null;
    setData(null);
    setError("");

    const load = async () => {
      try {
        const result = await getSiteAuditReport(clientId, auditId);
        if (!active) return;
        setData(result);
        if (!FINAL_STATUSES.includes(result.status)) {
          timer = setTimeout(load, REPORT_POLL_MS);
        }
      } catch (err) {
        console.error("Site audit report could not be read", err?.response?.data || err);
        if (active) setError("No se ha podido cargar la auditoría. Vuelve a intentarlo más tarde.");
      }
    };
    load();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [clientId, auditId]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Skeleton variant="rounded" height={220} sx={{ borderRadius: "12px" }} />;

  const { report } = data;
  if (!report) {
    return (
      <EmptyStateMessage
        icon={ClipboardCheck}
        message={
          FINAL_STATUSES.includes(data.status)
            ? `Auditoría ${STATUS_LABELS[data.status]?.toLowerCase() || "sin resultados"}.`
            : `${STATUS_LABELS[data.status] || "Analizando"}… ${data.pages || 0} páginas rastreadas. Se actualiza sola.`
        }
        sx={{ py: 4 }}
      />
    );
  }

  const severityCounts = Object.fromEntries(SEVERITIES.map(({ key, total }) => [key, report[total] || 0]));
  const problems = SEVERITIES.reduce((sum, { key }) => sum + severityCounts[key], 0);
  const band = scoreBand(report.score);
  const bandColor = band.color(palette);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
        <Block title="Puntuación de salud" help={BLOCK_HELP.score}>
          <Stack spacing={1}>
            {/* ScoreGauge trae su propia tarjeta; aquí ya va dentro de una. */}
            <Box sx={{ display: "flex", justifyContent: "center", "& > div": { border: 0, padding: 0 } }}>
              <ScoreGauge label={band.label} score={report.score} size={96} color={bandColor} />
            </Box>
            <LegendRow color={bandColor} label="Este sitio web" value={report.score} />
            <LegendRow color={palette.success.main} label="Recomendado" value={`${RECOMMENDED_SCORE}+`} />
          </Stack>
        </Block>

        <Block title="Problemas por tipo" help={BLOCK_HELP.types}>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1 }}>{problems}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Problemas
          </Typography>
          <SeverityBar counts={severityCounts} />
          <Box sx={{ mt: 1.5 }}>
            {SEVERITIES.map(({ key, label, color }) => (
              <LegendRow key={key} color={color} label={label} value={severityCounts[key]} />
            ))}
          </Box>
        </Block>

        <Block
          title="Rastreo"
          help={BLOCK_HELP.crawl}
          action={
            <Button variant="ghost" size="sm" onClick={() => setPagesOpen(true)}>
              Ver páginas
            </Button>
          }
        >
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1 }}>{report.pages ?? "—"}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Páginas rastreadas
          </Typography>
          <LegendRow color={palette.success.main} label="Comprobaciones superadas" value={report.passed} />
          <LegendRow color="error.main" label="Comprobaciones con problemas" value={report.issues.length} />
        </Block>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
        <IssuesBlock issues={report.issues} clientId={clientId} auditId={auditId} />
        <CategoriesBlock categories={report.categories} />
      </Box>

      <DomainBlock domain={report.domain} />

      <CrawledPagesModal open={pagesOpen} onOpenChange={setPagesOpen} clientId={clientId} auditId={auditId} />
    </Stack>
  );
}

AuditReport.propTypes = { clientId: PropTypes.number.isRequired, auditId: PropTypes.number.isRequired };

function AuditRow({ clientId, audit, open, onToggle }) {
  const theme = useTheme();
  return (
    <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", overflow: "hidden" }}>
      <Stack
        direction="row"
        spacing={2}
        onClick={onToggle}
        sx={{
          alignItems: "center",
          padding: 2,
          cursor: "pointer",
          backgroundColor: "background.paper",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {audit.title}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {audit.domain} · {new Date(audit.created_at).toLocaleDateString("es-ES")}
            {audit.runs > 1 ? ` · ${audit.runs} análisis` : ""}
          </Typography>
        </Box>
        <ChevronDown size={18} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ padding: 2, paddingTop: 0, backgroundColor: "background.paper" }}>
          <AuditReport clientId={clientId} auditId={audit.id} />
        </Box>
      </Collapse>
    </Box>
  );
}

AuditRow.propTypes = {
  clientId: PropTypes.number.isRequired,
  audit: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

function LaunchAuditModal({ open, onOpenChange, website, quota, onLaunch }) {
  const [form, setForm] = useState(() => emptyForm(website));
  const [isLaunching, setIsLaunching] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(emptyForm(website));
    }
  }, [open, website]);

  const setField = (key) => (value) => setForm((previous) => ({ ...previous, [key]: value }));

  const handleLaunch = async () => {
    setIsLaunching(true);
    try {
      await onLaunch(form);
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isLaunching && onOpenChange(next)}>
      <DialogHeader>
        <DialogTitle>Nueva auditoría web</DialogTitle>
      </DialogHeader>

      <DialogContent>
        <Stack spacing={2.5} sx={{ width: { xs: "100%", sm: 520 }, paddingTop: 1 }}>
          <Stack spacing={1}>
            <Label htmlFor="audit-domain">Dominio</Label>
            <CustomTextField
              id="audit-domain"
              value={form.domain}
              onChange={(event) => setField("domain")(event.target.value)}
              placeholder="Introduce el nombre del dominio"
              maxLength={255}
            />
          </Stack>
          <Stack spacing={1}>
            <Label htmlFor="audit-title">Nombre de la auditoría</Label>
            <CustomTextField
              id="audit-title"
              value={form.title}
              onChange={(event) => setField("title")(event.target.value)}
              placeholder="Nombre de la nueva auditoría"
              maxLength={300}
            />
          </Stack>
          <Stack spacing={1}>
            <Label>User agent</Label>
            <CustomSelector value={form.user_agent} onChange={setField("user_agent")} options={USER_AGENTS} />
          </Stack>
          <Stack spacing={1}>
            <Label>Renderizado de JavaScript</Label>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <ToggleSwitch
                checked={form.js_rendering}
                onChange={(event) => setField("js_rendering")(event.target.checked)}
              />
              <Typography variant="body2">{form.js_rendering ? "Activado" : "Desactivado"}</Typography>
            </Stack>
          </Stack>

          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Autorizar / desautorizar URL
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Opcional. Una ruta por línea; las directivas de desautorización tienen prioridad sobre las de
                autorización.
              </Typography>
            </Box>
            {URL_RULES.map(({ key, label, placeholder }) => (
              <Stack key={key} spacing={1}>
                <Label>{label}</Label>
                <CustomTextArea
                  value={form[key]}
                  onChange={(event) => setField(key)(event.target.value)}
                  placeholder={placeholder}
                  rows={3}
                />
              </Stack>
            ))}
          </Stack>

          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Si el dominio ya tiene auditoría, se vuelve a analizar la misma y se conserva su historial.
            {quota ? ` Se rastrean hasta ${quota.max_pages.toLocaleString("es-ES")} páginas.` : ""}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLaunching}>
          CANCELAR
        </Button>
        <Button onClick={handleLaunch} disabled={isLaunching || !form.domain.trim()}>
          {isLaunching ? <CircularProgress size={16} color="inherit" /> : "LANZAR AUDITORÍA"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

LaunchAuditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  website: PropTypes.string,
  quota: PropTypes.object,
  onLaunch: PropTypes.func.isRequired,
};

export function SiteAuditPanel({ clientId, website }) {
  const [audits, setAudits] = useState(null);
  const [quota, setQuota] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [popup, setPopup] = useState(null);

  const loadAudits = useCallback(async () => {
    try {
      const data = await getSiteAudits(clientId);
      setAudits(data.audits);
      setQuota(data.quota);
      return data.audits;
    } catch (err) {
      console.error("Site audits could not be listed", err?.response?.data || err);
      setLoadError("No se han podido cargar las auditorías del cliente.");
      return [];
    }
  }, [clientId]);

  useEffect(() => {
    setAudits(null);
    setOpenId(null);
    setLoadError("");
    loadAudits().then((list) => setOpenId(list[0]?.id ?? null));
  }, [clientId, loadAudits]);

  const handleLaunch = async (form) => {
    try {
      const { id } = await launchSiteAudit(clientId, form);
      await loadAudits();
      setOpenId(id);
      setModalOpen(false);
      setPopup({ message: "Auditoría lanzada. Los resultados aparecerán en unos minutos." });
    } catch (err) {
      console.error("Site audit could not be launched", err?.response?.data || err);
      setPopup({ message: launchError(err), error: true });
    }
  };

  const limitReached = Boolean(quota) && quota.used >= quota.limit;

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Auditorías web
                </Typography>
                {quota && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {quota.used} de {quota.limit} auditorías este mes
                  </Typography>
                )}
              </Box>
              <Button
                onClick={() => setModalOpen(true)}
                disabled={!quota || limitReached}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Plus size={16} />
                Nueva auditoría
              </Button>
            </Stack>

            {limitReached && (
              <Alert severity="info">
                Este cliente ya ha usado las auditorías de este mes. Podrás lanzar otra el mes que viene.
              </Alert>
            )}
            {loadError && <Alert severity="error">{loadError}</Alert>}
            {!audits && !loadError && <Skeleton variant="rounded" height={64} sx={{ borderRadius: "12px" }} />}
            {audits?.length === 0 && (
              <EmptyStateMessage
                icon={ClipboardCheck}
                message="Todavía no se ha lanzado ninguna auditoría."
                sx={{ py: 4 }}
              />
            )}

            {audits?.map((audit) => (
              <AuditRow
                key={audit.id}
                clientId={clientId}
                audit={audit}
                open={audit.id === openId}
                onToggle={() => setOpenId((current) => (current === audit.id ? null : audit.id))}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      <LaunchAuditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        website={website}
        quota={quota}
        onLaunch={handleLaunch}
      />

      <SuccessPopup
        message={popup?.message || ""}
        isOpen={Boolean(popup)}
        error={Boolean(popup?.error)}
        autoHideDuration={popup?.error ? 6000 : 3000}
        onClose={() => setPopup(null)}
      />
    </Stack>
  );
}

SiteAuditPanel.propTypes = {
  clientId: PropTypes.number.isRequired,
  website: PropTypes.string,
};

export default SiteAuditPanel;
